import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { TherappiWelcome, getWelcomeContent, hasSeenWelcome } from './TherappiWelcome';
import { useTourController, isTourActiveGlobal } from '@/hooks/useTourController';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { 
  logFlight,
  getOnboardingState,
  isHydrated,
  isAuthHydrated
} from '@/hooks/useOnboardingStateMachine';
import { diagLog } from '@/utils/diagnosticSession';

// localStorage keys for pre-auth welcome (check BOTH for migration compatibility)
const PRE_AUTH_WELCOME_CANONICAL_KEY = 'therappi_pre_auth_welcome_seen';
const PRE_AUTH_WELCOME_LEGACY_KEY = 'therappi-pre-auth-welcome-seen';

/**
 * TherappiWelcomeHandler - Shows typewriter welcome after onboarding
 * 
 * Uses the OnboardingStateMachine as SINGLE SOURCE OF TRUTH.
 * Only renders when state === 'welcome'.
 * 
 * DOES NOT perform any routing or redirects.
 * Only renders the TherappiWelcome component when appropriate.
 */
export const TherappiWelcomeHandler: React.FC = () => {
  const location = useLocation();
  const tourController = useTourController();
  const [user, setUser] = useState<User | null>(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // CRITICAL: Prevent double Welcome display with a ref
  const hasShownWelcomeRef = useRef(false);
  
  // Check if running on native platform
  const isNative = Capacitor.isNativePlatform();
  
  // Track if the tour has ACTUALLY started (not just preparing/transitioning)
  const [tourHasStarted, setTourHasStarted] = useState(false);

  // Get user for display purposes
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Subscribe to tour events - only hide when tour actually starts
  useEffect(() => {
    const unsubscribe = tourController.subscribe((action) => {
      if (action === 'start-tour') {
        logFlight('WELCOME_HIDING', { reason: 'tour_started' });
        setTourHasStarted(true);
      } else if (action === 'end-tour') {
        logFlight('WELCOME_TOUR_ENDED', {});
        setTourHasStarted(false);
      }
    });
    return () => unsubscribe();
  }, [tourController]);

  // Re-render periodically to catch state changes
  useEffect(() => {
    const interval = setInterval(() => {
      setForceUpdate(f => f + 1);
    }, 750);
    return () => clearInterval(interval);
  }, []);

  // Get current state from state machine
  const state = getOnboardingState();
  const hydrated = isHydrated();
  const authHydrated = isAuthHydrated();

  // Use storage key based on path
  const storageKey = location.pathname;
  
  // CRITICAL: Block post-auth typewriter on / and /dashboard
  // These were already shown during pre-auth typewriter flow
  const isBlockedPath = location.pathname === '/' || location.pathname === '/dashboard';
  const content = isBlockedPath ? null : getWelcomeContent(location.pathname);
  
  // On native, check if PreAuthWelcome has been seen (check BOTH key variants)
  const hasSeenPreAuth = (() => {
    try {
      return localStorage.getItem(PRE_AUTH_WELCOME_CANONICAL_KEY) === 'true' ||
             localStorage.getItem(PRE_AUTH_WELCOME_LEGACY_KEY) === 'true';
    } catch {
      return false;
    }
  })();
  
  // DIAGNOSTIC: Log render decision with reason
  let renderDecision = 'WILL_RENDER';
  let nullReason = '';
  
  if (tourHasStarted || isTourActiveGlobal()) {
    renderDecision = 'NULL';
    nullReason = 'tour active';
  } else if (isNative && !hasSeenPreAuth) {
    renderDecision = 'NULL';
    nullReason = 'native: pre-auth not seen yet';
  } else if (!authHydrated) {
    renderDecision = 'DEFER';
    nullReason = 'waiting for auth hydration';
  } else if (!hydrated) {
    renderDecision = 'DEFER';
    nullReason = 'waiting for full hydration';
  } else if (!user) {
    renderDecision = 'NULL';
    nullReason = 'no user after hydration';
  } else if (state !== 'app') {
    // Typewriter welcome only shows in 'app' state now (after tour)
    // Pre-auth typewriter is handled separately
    renderDecision = 'NULL';
    nullReason = `state=${state} (not app)`;
  } else if (!content) {
    renderDecision = 'NULL';
    nullReason = 'no content for path';
  } else if (hasSeenWelcome(storageKey)) {
    renderDecision = 'NULL';
    nullReason = 'already seen';
  } else if (hasShownWelcomeRef.current) {
    renderDecision = 'NULL';
    nullReason = 'already shown once this session';
  }

  diagLog('TherappiWelcomeHandler', 'RENDER', {
    path: location.pathname,
    state,
    authHydrated,
    hydrated,
    isNative,
    hasSeenPreAuth,
    hasUser: !!user,
    tourHasStarted,
    hasContent: !!content,
    hasSeen: hasSeenWelcome(storageKey),
    hasShownOnce: hasShownWelcomeRef.current,
    renderDecision,
    nullReason
  });

  // RULE: Don't render if tour has actually started or is active
  if (tourHasStarted || isTourActiveGlobal()) {
    return null;
  }

  // RULE: On native, don't render until PreAuthWelcome has been seen
  if (isNative && !hasSeenPreAuth) {
    return null;
  }

  // RULE: DEFER during hydration - OnboardingWrapper shows loading
  if (!authHydrated || !hydrated) {
    return null;
  }

  // RULE: Don't render without authenticated user (after hydration)
  if (!user) {
    return null;
  }

  // RULE: Only show when state is 'app' (post-tour page welcome)
  // The pre-auth typewriter is now handled in the unified flow
  if (state !== 'app') {
    return null;
  }

  // Don't render if no content for this page or already seen
  if (!content || hasSeenWelcome(storageKey)) {
    return null;
  }

  // CRITICAL: Prevent duplicate display
  if (hasShownWelcomeRef.current) {
    return null;
  }

  // Mark that we're showing welcome (single-run guard)
  hasShownWelcomeRef.current = true;

  return (
    <TherappiWelcome
      pageKey={storageKey}
      title={content.title}
      message={content.message}
      tips={content.tips}
      buttonText={content.buttonText}
    />
  );
};
